import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Users, 
  CurrencyDollar, 
  ChartLineUp,
  Copy,
  SignOut,
  House,
  Calendar,
  Clock,
  CheckCircle,
  Spinner,
  Medal,
  Trophy,
  MapPin,
  Star,
  Crown,
  Bank,
  ArrowRight,
  Warning,
  CreditCard
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier display configuration
const TIER_CONFIG = {
  base: { label: 'Base', color: 'text-zinc-400', bgColor: 'bg-zinc-500/10', icon: Star },
  silver: { label: 'Silver', color: 'text-gray-300', bgColor: 'bg-gray-300/10', icon: Medal },
  gold: { label: 'Gold', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10', icon: Trophy },
  platinum: { label: 'Platinum', color: 'text-blue-400', bgColor: 'bg-blue-400/10', icon: Crown },
  founder: { label: 'Founder', color: 'text-[#D4AF37]', bgColor: 'bg-[#D4AF37]/10', icon: Crown },
};

const TIER_MILESTONES = [
  { hosts: 0, tier: 'base', rate: 0.50 },
  { hosts: 25, tier: 'silver', rate: 0.60 },
  { hosts: 50, tier: 'gold', rate: 0.70 },
  { hosts: 100, tier: 'platinum', rate: 0.85 },
  { hosts: 200, tier: 'founder', rate: 1.00 },
];

export default function AffiliateDashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [referredHosts, setReferredHosts] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [payoutBalance, setPayoutBalance] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('affiliate_token');
    if (!token) {
      navigate('/affiliate/login');
      return;
    }
    loadDashboardData();
    
    // Check if returning from Stripe onboarding
    if (searchParams.get('stripe_connected')) {
      toast.success('Bank account connected successfully!');
      loadStripeStatus();
    } else if (searchParams.get('stripe_refresh')) {
      toast.info('Please complete your bank account setup');
      handleConnectBank();
    }
  }, [navigate, searchParams]);

  const loadDashboardData = async () => {
    const token = localStorage.getItem('affiliate_token');
    
    try {
      // Load affiliate profile
      const profileRes = await fetch(`${API_URL}/api/affiliate/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!profileRes.ok) {
        if (profileRes.status === 401 || profileRes.status === 403) {
          localStorage.removeItem('affiliate_token');
          localStorage.removeItem('affiliate_data');
          navigate('/affiliate/login');
          return;
        }
        throw new Error('Failed to load profile');
      }
      
      const profileData = await profileRes.json();
      setAffiliate(profileData);

      // Load referred hosts
      const hostsRes = await fetch(`${API_URL}/api/affiliate/referred-hosts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (hostsRes.ok) {
        const hostsData = await hostsRes.json();
        setReferredHosts(hostsData);
      }

      // Load earnings
      const earningsRes = await fetch(`${API_URL}/api/affiliate/earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (earningsRes.ok) {
        const earningsData = await earningsRes.json();
        setEarnings(earningsData);
      }
      
      // Load Stripe Connect status and balance
      await loadStripeStatus();
      await loadPayoutBalance();
      await loadPayoutHistory();
      
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadStripeStatus = async () => {
    const token = localStorage.getItem('affiliate_token');
    try {
      const res = await fetch(`${API_URL}/api/affiliate/connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStripeStatus(data);
      }
    } catch (error) {
      console.error('Failed to load Stripe status:', error);
    }
  };
  
  const loadPayoutBalance = async () => {
    const token = localStorage.getItem('affiliate_token');
    try {
      const res = await fetch(`${API_URL}/api/affiliate/connect/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPayoutBalance(data);
      }
    } catch (error) {
      console.error('Failed to load payout balance:', error);
    }
  };
  
  const loadPayoutHistory = async () => {
    const token = localStorage.getItem('affiliate_token');
    try {
      const res = await fetch(`${API_URL}/api/affiliate/connect/payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPayoutHistory(data.payouts || []);
      }
    } catch (error) {
      console.error('Failed to load payout history:', error);
    }
  };
  
  const handleConnectBank = async () => {
    const token = localStorage.getItem('affiliate_token');
    setConnectLoading(true);
    
    try {
      // First, create account if needed
      if (!stripeStatus?.connected) {
        const createRes = await fetch(`${API_URL}/api/affiliate/connect/create-account`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ country: 'US' })
        });
        
        if (!createRes.ok) {
          const error = await createRes.json();
          throw new Error(error.detail || 'Failed to create account');
        }
      }
      
      // Get onboarding link
      const linkRes = await fetch(`${API_URL}/api/affiliate/connect/onboarding-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!linkRes.ok) {
        const error = await linkRes.json();
        throw new Error(error.detail || 'Failed to get onboarding link');
      }
      
      const { onboarding_url } = await linkRes.json();
      window.location.href = onboarding_url;
      
    } catch (error) {
      toast.error(error.message || 'Failed to connect bank account');
      setConnectLoading(false);
    }
  };
  
  const handleRequestPayout = async () => {
    const token = localStorage.getItem('affiliate_token');
    setPayoutLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/affiliate/connect/request-payout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({})  // Payout entire balance
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to request payout');
      }
      
      toast.success(data.message || 'Payout requested successfully!');
      
      // Refresh data
      await loadPayoutBalance();
      await loadPayoutHistory();
      await loadDashboardData();
      
    } catch (error) {
      toast.error(error.message || 'Failed to request payout');
    } finally {
      setPayoutLoading(false);
    }
  };
  
  const handleOpenStripeDashboard = async () => {
    const token = localStorage.getItem('affiliate_token');
    
    try {
      const res = await fetch(`${API_URL}/api/affiliate/connect/dashboard-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to get dashboard link');
      }
      
      const { dashboard_url } = await res.json();
      window.open(dashboard_url, '_blank');
      
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const copyReferralLink = () => {
    if (affiliate?.referral_link) {
      navigator.clipboard.writeText(affiliate.referral_link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate_data');
    navigate('/affiliate/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Spinner size={48} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'hosts', label: 'Referred Hosts' },
    { id: 'earnings', label: 'Earnings' },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-serif text-[#D4AF37]">100Nook</h1>
              <span className="text-zinc-500">|</span>
              <span className="text-white">Affiliate Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-zinc-400 text-sm hidden sm:block">
                Welcome, {affiliate?.full_name}
              </span>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/5"
              >
                <SignOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tier Progress Card */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            {/* Current Tier */}
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full ${TIER_CONFIG[affiliate?.tier || 'base']?.bgColor} flex items-center justify-center`}>
                {React.createElement(TIER_CONFIG[affiliate?.tier || 'base']?.icon || Star, {
                  size: 32,
                  weight: "fill",
                  className: TIER_CONFIG[affiliate?.tier || 'base']?.color
                })}
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Current Tier</p>
                <p className={`text-2xl font-serif ${TIER_CONFIG[affiliate?.tier || 'base']?.color}`}>
                  {TIER_CONFIG[affiliate?.tier || 'base']?.label}
                  {affiliate?.is_founder && <span className="ml-2 text-sm">(Lifetime)</span>}
                </p>
                <p className="text-white text-lg font-semibold">
                  ${affiliate?.commission_rate?.toFixed(2) || '0.50'} <span className="text-zinc-400 text-sm font-normal">per booking</span>
                </p>
              </div>
            </div>

            {/* Progress to Next Tier */}
            {!affiliate?.is_founder && affiliate?.next_tier_name && (
              <div className="flex-1 max-w-md">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-400">Progress to {TIER_CONFIG[affiliate?.next_tier_name]?.label || affiliate?.next_tier_name}</span>
                  <span className="text-white">{affiliate?.active_hosts || 0} hosts</span>
                </div>
                <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${TIER_CONFIG[affiliate?.next_tier_name]?.bgColor?.replace('/10', '')} transition-all duration-500`}
                    style={{ 
                      width: `${Math.min(100, ((affiliate?.active_hosts || 0) / (TIER_MILESTONES.find(m => m.tier === affiliate?.next_tier_name)?.hosts || 25)) * 100)}%`,
                      backgroundColor: affiliate?.next_tier_name === 'founder' ? '#D4AF37' : 
                                       affiliate?.next_tier_name === 'platinum' ? '#60a5fa' :
                                       affiliate?.next_tier_name === 'gold' ? '#facc15' :
                                       affiliate?.next_tier_name === 'silver' ? '#d1d5db' : '#71717a'
                    }}
                  />
                </div>
                <p className="text-zinc-500 text-xs mt-1">
                  {affiliate?.hosts_to_next_tier || 0} more hosts to unlock ${TIER_MILESTONES.find(m => m.tier === affiliate?.next_tier_name)?.rate?.toFixed(2) || '0.00'}/booking
                </p>
              </div>
            )}
          </div>

          {/* Founder Tier Progress */}
          {!affiliate?.is_founder && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Crown size={24} className="text-[#D4AF37]" />
                  <div>
                    <p className="text-white font-semibold">Founder Tier Progress</p>
                    <p className="text-zinc-400 text-sm">Refer 200 hosts within 2 years for $1.00/booking FOR LIFE</p>
                  </div>
                </div>
                <div className="flex-1 max-w-sm">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">{affiliate?.founder_progress || 0} / 200 hosts</span>
                    {affiliate?.founder_window_ends && (
                      <span className="text-zinc-500">
                        Window ends: {new Date(affiliate.founder_window_ends).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B5952F]"
                      style={{ width: `${Math.min(100, ((affiliate?.founder_progress || 0) / 200) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Founder Badge for existing founders */}
          {affiliate?.is_founder && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-3 bg-[#D4AF37]/10 p-4 rounded-lg">
                <Crown size={32} weight="fill" className="text-[#D4AF37]" />
                <div>
                  <p className="text-[#D4AF37] font-semibold">Founder Tier - Lifetime Member</p>
                  <p className="text-zinc-400 text-sm">You've unlocked $1.00/booking for life! Thank you for being an early supporter.</p>
                  {affiliate?.founder_locked_at && (
                    <p className="text-zinc-500 text-xs mt-1">Achieved: {new Date(affiliate.founder_locked_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Badges Section */}
        {(affiliate?.state_badges?.length > 0 || affiliate?.zip_badges?.length > 0) && (
          <div className="bg-[#121212] border border-white/10 p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Medal size={24} className="text-[#D4AF37]" />
              Your Badges
            </h3>
            <div className="flex flex-wrap gap-3">
              {affiliate?.state_badges?.map(state => (
                <div key={state} className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-4 py-2 rounded-full">
                  <MapPin size={16} className="text-blue-400" />
                  <span className="text-blue-300 font-medium">{state} State Builder</span>
                </div>
              ))}
              {affiliate?.zip_badges?.map(zip => (
                <div key={zip} className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-4 py-2 rounded-full">
                  <House size={16} className="text-green-400" />
                  <span className="text-green-300 font-medium">Local Ambassador ({zip})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-3">
              <Users size={24} className="text-[#D4AF37]" />
              <span className="text-zinc-400 text-sm">Referred Hosts</span>
            </div>
            <div className="text-3xl font-serif text-white">{affiliate?.total_referred_hosts || 0}</div>
            <div className="text-zinc-500 text-sm mt-1">{affiliate?.active_hosts || 0} active</div>
          </div>

          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-3">
              <ChartLineUp size={24} className="text-[#D4AF37]" />
              <span className="text-zinc-400 text-sm">Total Bookings</span>
            </div>
            <div className="text-3xl font-serif text-white">{affiliate?.total_bookings || 0}</div>
            <div className="text-zinc-500 text-sm mt-1">from your hosts</div>
          </div>

          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-3">
              <Clock size={24} className="text-yellow-500" />
              <span className="text-zinc-400 text-sm">Pending Earnings</span>
            </div>
            <div className="text-3xl font-serif text-yellow-500">${affiliate?.pending_earnings?.toFixed(2) || '0.00'}</div>
            <div className="text-zinc-500 text-sm mt-1">clearing soon</div>
          </div>

          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-3">
              <CurrencyDollar size={24} className="text-green-500" />
              <span className="text-zinc-400 text-sm">Lifetime Earnings</span>
            </div>
            <div className="text-3xl font-serif text-green-500">${affiliate?.lifetime_earnings?.toFixed(2) || '0.00'}</div>
            <div className="text-zinc-500 text-sm mt-1">${affiliate?.paid_earnings?.toFixed(2) || '0.00'} paid</div>
          </div>
        </div>

        {/* Payouts Section */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Bank size={24} className="text-[#D4AF37]" />
              Payouts
            </h3>
            {stripeStatus?.connected && stripeStatus?.payouts_enabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenStripeDashboard}
                className="border-white/20 text-white hover:bg-white/5"
              >
                <CreditCard size={16} className="mr-2" />
                Stripe Dashboard
              </Button>
            )}
          </div>

          {/* Bank Connection Status */}
          {!stripeStatus?.connected ? (
            <div className="bg-[#0a0a0a] border border-white/5 p-6 text-center">
              <Bank size={48} className="text-zinc-600 mx-auto mb-4" />
              <h4 className="text-white font-semibold mb-2">Connect Your Bank Account</h4>
              <p className="text-zinc-400 text-sm mb-4">
                Connect your bank account to receive payouts for your commissions. 
                Powered by Stripe for secure and fast transfers.
              </p>
              <Button
                onClick={handleConnectBank}
                disabled={connectLoading}
                className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                data-testid="connect-bank-btn"
              >
                {connectLoading ? (
                  <Spinner size={20} className="animate-spin mr-2" />
                ) : (
                  <Bank size={20} className="mr-2" />
                )}
                Connect Bank Account
              </Button>
            </div>
          ) : !stripeStatus?.payouts_enabled ? (
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-6">
              <div className="flex items-start gap-4">
                <Warning size={32} className="text-yellow-500 flex-shrink-0" />
                <div>
                  <h4 className="text-yellow-500 font-semibold mb-2">Complete Your Account Setup</h4>
                  <p className="text-yellow-200/70 text-sm mb-4">
                    Your Stripe account needs additional information before you can receive payouts.
                  </p>
                  <Button
                    onClick={handleConnectBank}
                    disabled={connectLoading}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                  >
                    Complete Setup
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Balance & Request Payout */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#0a0a0a] border border-white/5 p-4">
                  <p className="text-zinc-400 text-sm mb-1">Available for Payout</p>
                  <p className="text-2xl font-serif text-green-500">{payoutBalance?.available_display || '$0.00'}</p>
                  <p className="text-zinc-500 text-xs mt-1">Cleared commissions</p>
                </div>
                <div className="bg-[#0a0a0a] border border-white/5 p-4">
                  <p className="text-zinc-400 text-sm mb-1">Pending Clearance</p>
                  <p className="text-2xl font-serif text-yellow-500">{payoutBalance?.pending_display || '$0.00'}</p>
                  <p className="text-zinc-500 text-xs mt-1">Clears 7 days after booking</p>
                </div>
                <div className="bg-[#0a0a0a] border border-white/5 p-4">
                  <p className="text-zinc-400 text-sm mb-1">Total Paid Out</p>
                  <p className="text-2xl font-serif text-white">{payoutBalance?.paid_display || '$0.00'}</p>
                  <p className="text-zinc-500 text-xs mt-1">Lifetime payouts</p>
                </div>
              </div>

              {/* Request Payout Button */}
              <div className="flex items-center justify-between bg-[#0a0a0a] border border-white/5 p-4">
                <div>
                  <p className="text-white font-medium">Request Payout</p>
                  <p className="text-zinc-400 text-sm">
                    Minimum payout: {payoutBalance?.minimum_display || '$10.00'}
                  </p>
                </div>
                <Button
                  onClick={handleRequestPayout}
                  disabled={!payoutBalance?.can_request_payout || payoutLoading}
                  className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                  data-testid="request-payout-btn"
                >
                  {payoutLoading ? (
                    <Spinner size={20} className="animate-spin mr-2" />
                  ) : (
                    <CurrencyDollar size={20} className="mr-2" />
                  )}
                  {payoutBalance?.can_request_payout 
                    ? `Payout ${payoutBalance?.available_display}`
                    : 'Minimum not met'}
                </Button>
              </div>

              {/* Payout History */}
              {payoutHistory.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3">Recent Payouts</h4>
                  <div className="space-y-2">
                    {payoutHistory.slice(0, 5).map((payout) => (
                      <div 
                        key={payout.id} 
                        className="flex items-center justify-between bg-[#0a0a0a] border border-white/5 p-3"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            payout.status === 'processing' ? 'bg-yellow-500' :
                            payout.status === 'completed' ? 'bg-green-500' : 'bg-red-500'
                          }`} />
                          <div>
                            <p className="text-white text-sm">${payout.amount_dollars?.toFixed(2)}</p>
                            <p className="text-zinc-500 text-xs">
                              {new Date(payout.requested_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          payout.status === 'processing' ? 'bg-yellow-500/10 text-yellow-500' :
                          payout.status === 'completed' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                        }`}>
                          {payout.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Referral Link */}
        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Your Referral Link</h2>
              <p className="text-zinc-400 text-sm">Share this link with potential hosts</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-[#0a0a0a] border border-white/10 px-4 py-2 rounded text-white font-mono text-sm truncate max-w-md">
                {affiliate?.referral_link}
              </div>
              <Button
                onClick={copyReferralLink}
                className="bg-[#D4AF37] text-black hover:bg-[#C4A030]"
              >
                <Copy size={16} className="mr-2" />
                Copy
              </Button>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#D4AF37]/20">
            <p className="text-zinc-400 text-sm">
              <span className="text-[#D4AF37] font-medium">Your Referral Code:</span>{' '}
              <span className="text-white font-mono">{affiliate?.referral_code}</span>
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-white/10 mb-6">
          <div className="flex gap-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Getting Started</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={16} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Account Approved</p>
                    <p className="text-zinc-400 text-sm">Your affiliate account is active</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4AF37] text-sm font-medium">2</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">Share Your Link</p>
                    <p className="text-zinc-400 text-sm">Share your referral link with homeowners who have spaces to rent</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4AF37] text-sm font-medium">3</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">Earn Up to $1 Per Booking</p>
                    <p className="text-zinc-400 text-sm">Start at $0.50, unlock up to $1.00 as you grow</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payouts Card on Overview Tab */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Bank size={20} className="text-[#D4AF37]" />
                Payouts
              </h3>
              {!stripeStatus?.connected ? (
                <div className="text-center py-4">
                  <p className="text-zinc-400 text-sm mb-3">Connect your bank account to receive payouts</p>
                  <Button
                    onClick={handleConnectBank}
                    disabled={connectLoading}
                    size="sm"
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  >
                    {connectLoading ? <Spinner size={16} className="animate-spin mr-2" /> : <Bank size={16} className="mr-2" />}
                    Connect Bank
                  </Button>
                </div>
              ) : !stripeStatus?.payouts_enabled ? (
                <div className="text-center py-4">
                  <p className="text-yellow-500 text-sm mb-3">Complete your Stripe setup to enable payouts</p>
                  <Button
                    onClick={handleConnectBank}
                    size="sm"
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    Complete Setup
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Available</span>
                    <span className="text-green-500 font-semibold">{payoutBalance?.available_display || '$0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Pending</span>
                    <span className="text-yellow-500 font-semibold">{payoutBalance?.pending_display || '$0.00'}</span>
                  </div>
                  {payoutBalance?.can_request_payout && (
                    <Button
                      onClick={handleRequestPayout}
                      disabled={payoutLoading}
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700 text-white mt-2"
                    >
                      {payoutLoading ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                      Request Payout
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Earnings Breakdown</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-zinc-400 text-sm">Pending</p>
                  <p className="text-yellow-500 text-xl font-semibold">${affiliate?.pending_earnings?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Cleared</p>
                  <p className="text-blue-500 text-xl font-semibold">${affiliate?.cleared_earnings?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Paid</p>
                  <p className="text-green-500 text-xl font-semibold">${affiliate?.paid_earnings?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Lifetime</p>
                  <p className="text-white text-xl font-semibold">${affiliate?.lifetime_earnings?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hosts' && (
          <div className="bg-[#121212] border border-white/10">
            {referredHosts.length === 0 ? (
              <div className="p-12 text-center">
                <House size={48} className="text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No hosts yet</h3>
                <p className="text-zinc-400 mb-6">Share your referral link to start referring hosts</p>
                <Button onClick={copyReferralLink} className="bg-[#D4AF37] text-black hover:bg-[#C4A030]">
                  <Copy size={16} className="mr-2" />
                  Copy Referral Link
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Host</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Date Referred</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Listings</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Bookings</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referredHosts.map((host) => (
                      <tr key={host.id} className="border-b border-white/5">
                        <td className="py-4 px-6">
                          <div>
                            <p className="text-white font-medium">{host.name}</p>
                            <p className="text-zinc-500 text-sm">{host.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-zinc-400">
                          {new Date(host.date_referred).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6 text-white">{host.listings_count}</td>
                        <td className="py-4 px-6 text-white">{host.bookings_count}</td>
                        <td className="py-4 px-6 text-green-500 font-medium">
                          ${host.total_earnings?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="bg-[#121212] border border-white/10">
            {earnings.length === 0 ? (
              <div className="p-12 text-center">
                <CurrencyDollar size={48} className="text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No earnings yet</h3>
                <p className="text-zinc-400">Earnings will appear here when your referred hosts get bookings</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Date</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Host</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Booking Amount</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Commission</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.map((earning) => (
                      <tr key={earning.id} className="border-b border-white/5">
                        <td className="py-4 px-6 text-zinc-400">
                          {new Date(earning.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6 text-white">{earning.host_name}</td>
                        <td className="py-4 px-6 text-white">${earning.booking_amount?.toFixed(2)}</td>
                        <td className="py-4 px-6 text-green-500 font-medium">
                          ${earning.commission_amount?.toFixed(2)}
                        </td>
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            earning.status === 'paid' 
                              ? 'bg-green-500/10 text-green-500'
                              : earning.status === 'cleared'
                              ? 'bg-blue-500/10 text-blue-500'
                              : 'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {earning.status.charAt(0).toUpperCase() + earning.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
